import React from "react";
import { AlertTriangle, ShieldCheck, Clock, MapPin, ChevronRight } from "lucide-react";
import { TRUST_STYLE, ageLabel } from "../data/provenance";
import { INCIDENT_TYPES, SEVERITY } from "../data/operational";

/* Right panel (§7): active alerts, the selected object's detail, and the
 * engines' recommendations.
 *
 * Every block that renders operational data also renders where that data came
 * from. That is the §9 requirement made concrete — a trust badge next to the
 * number, not a disclaimer at the bottom of the page. */
export default function IntelPanel({
  lang, selected, incidents, decisions, ops, onFocus, onDismissSelection, onCommand,
}) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";

  return (
    <div className="flex flex-col">
      {/* Operational KPIs */}
      <section className="px-3 py-3 border-b border-slate-200">
        <Head>{lang === "mr" ? "कार्यान्वयन स्थिती" : "Operational Status"}</Head>
        <div className="grid grid-cols-2 gap-2">
          <Kpi label={lang === "mr" ? "एकूण भार" : "Total load"} value={`${ops.loadPct}%`} />
          <Kpi label={lang === "mr" ? "सक्रिय घटना" : "Active incidents"} value={ops.incidentsActive} />
          <Kpi label={lang === "mr" ? "लाल झोन" : "Red zones"} value={ops.zonesRed} tone="red" />
          <Kpi label={lang === "mr" ? "अंबर झोन" : "Amber zones"} value={ops.zonesAmber} tone="amber" />
        </div>
        <TrustLine trust={ops.trust} />
      </section>

      {/* Selected feature detail */}
      {selected && (
        <section className="px-3 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between gap-2">
            <Head>{lang === "mr" ? "निवडलेला घटक" : "Selection"}</Head>
            <button
              type="button"
              onClick={onDismissSelection}
              className="text-[10px] font-bold text-slate-400 hover:text-slate-600"
            >
              {lang === "mr" ? "बंद" : "Clear"}
            </button>
          </div>
          <div className="text-[13px] font-black text-slate-900">{selected.name}</div>
          <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
            {selected.id && <Field k="ID" v={selected.id} />}
            {selected.typeLabel && <Field k={lang === "mr" ? "प्रकार" : "Type"} v={selected.typeLabel} />}
            {selected.severity && (
              <Field k={lang === "mr" ? "तीव्रता" : "Severity"} v={L(SEVERITY[selected.severity]?.label)} />
            )}
            {selected.status && <Field k={lang === "mr" ? "स्थिती" : "Status"} v={selected.status} />}
            {selected.ratioPct != null && (
              <Field k={lang === "mr" ? "भरणा" : "Occupancy"} v={`${selected.ratioPct}%`} />
            )}
            {selected.owner && <Field k={lang === "mr" ? "स्रोत मालक" : "Data owner"} v={selected.owner} />}
            {selected.source && <Field k={lang === "mr" ? "स्रोत" : "Source"} v={selected.source} />}
          </div>
          {selected.trust && <TrustLine trust={selected.trust} />}
          {selected.note && (
            <p className="mt-1.5 text-[10px] leading-snug text-slate-500 italic">{selected.note}</p>
          )}
        </section>
      )}

      {/* Active incidents */}
      <section className="px-3 py-3 border-b border-slate-200">
        <Head>{lang === "mr" ? "सक्रिय घटना" : "Active Incidents"}</Head>
        {incidents.length === 0 ? (
          <Empty>{lang === "mr" ? "कोणतीही सक्रिय घटना नाही" : "No active incidents"}</Empty>
        ) : (
          <ul className="space-y-1.5">
            {incidents.map((i) => (
              <li key={i.id}>
                <button
                  type="button"
                  onClick={() => onFocus({ coord: i.coord, zoom: 16 })}
                  className="w-full text-left rounded-lg border border-slate-200 bg-white p-2 hover:border-slate-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 rounded-full flex-shrink-0"
                      style={{ background: SEVERITY[i.severity]?.color }}
                    />
                    <span className="text-[11px] font-black text-slate-800">{i.id}</span>
                    <span className="ml-auto text-[9px] font-bold text-slate-400">
                      {ageLabel(i)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-600 truncate">
                    {L(INCIDENT_TYPES[i.type]?.label)} · {L(i.location)}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1">
                    <Badge trust={i.provenance?.trust} />
                    <span className="text-[9px] text-slate-400 truncate">{i.status}</span>
                    {onCommand && (
                      /* Opens the workflow. A span, not a nested <button> —
                         a button inside a button is invalid HTML and the
                         inner click would not fire reliably. */
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); onCommand(i); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault(); e.stopPropagation(); onCommand(i);
                          }
                        }}
                        className="ml-auto cursor-pointer rounded bg-slate-800 px-1.5 py-0.5 text-[9px] font-black text-white hover:bg-slate-700"
                      >
                        {lang === "mr" ? "कमांड" : "COMMAND"}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Decision intelligence */}
      <section className="px-3 py-3">
        <Head>{lang === "mr" ? "एआय शिफारसी" : "AI Recommendations"}</Head>
        {decisions.length === 0 ? (
          <Empty>
            {lang === "mr"
              ? "सध्या कोणतीही शिफारस नाही"
              : "No recommendations — all zones within thresholds"}
          </Empty>
        ) : (
          <ul className="space-y-2">
            {decisions.map((d) => (
              <li
                key={d.id}
                className="rounded-lg border border-slate-200 bg-white p-2.5"
              >
                <div className="flex items-start gap-1.5">
                  {d.priority === "critical" ? (
                    <AlertTriangle size={13} className="text-red-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <ShieldCheck size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-slate-900 leading-snug">
                      {L(d.title)}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-600 leading-snug">
                      {L(d.rationale)}
                    </div>
                  </div>
                </div>

                {/* Evidence is the whole point — a recommendation without it
                    is an assertion. */}
                <div className="mt-1.5 rounded bg-slate-50 border border-slate-200 px-1.5 py-1">
                  <div className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                    {lang === "mr" ? "पुरावा" : "Evidence"}
                  </div>
                  {d.evidence.map((e) => (
                    <div key={e} className="text-[10px] text-slate-600 truncate">· {e}</div>
                  ))}
                </div>

                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <Badge trust={d.trust} />
                  {d.confidence != null && (
                    <span className="text-[9px] font-bold text-slate-500">
                      {lang === "mr" ? "नियम विश्वास" : "Rule confidence"} {Math.round(d.confidence * 100)}%
                    </span>
                  )}
                  {d.requiresApproval && (
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">
                      {lang === "mr" ? "मानवी मंजुरी आवश्यक" : "HUMAN APPROVAL REQUIRED"}
                    </span>
                  )}
                  {d.focus && (
                    <button
                      type="button"
                      onClick={() => onFocus({ coord: d.focus, zoom: 14.5 })}
                      className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-bold text-saffron-dark hover:underline"
                    >
                      <MapPin size={9} />
                      {lang === "mr" ? "नकाशावर" : "Show"}
                      <ChevronRight size={9} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const Head = ({ children }) => (
  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">{children}</div>
);

const Empty = ({ children }) => (
  <div className="rounded-lg border border-dashed border-slate-200 px-2 py-3 text-center text-[11px] text-slate-400">
    {children}
  </div>
);

const Field = ({ k, v }) => (
  <div className="flex gap-1.5">
    <span className="text-slate-400 flex-shrink-0">{k}:</span>
    <span className="font-semibold text-slate-700 truncate">{v}</span>
  </div>
);

function Kpi({ label, value, tone }) {
  const toneCls =
    tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
      <div className={`text-lg font-black leading-none tabular-nums ${toneCls}`}>{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

function Badge({ trust }) {
  const s = TRUST_STYLE[trust];
  if (!s) return null;
  return (
    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function TrustLine({ trust }) {
  const s = TRUST_STYLE[trust];
  if (!s) return null;
  return (
    <div className="mt-2 flex items-center gap-1.5">
      <Badge trust={trust} />
      <span className="text-[9px] text-slate-400 flex items-center gap-0.5">
        <Clock size={8} /> derived from current map state
      </span>
    </div>
  );
}
